import { allowSwapRequest, checkIfManageSwapRequest } from '../../helpers/swapScheduleHelper'
import { capitalizeFirstLetter, customDeepClone, getFirstLetterUpperCase, getFirstThreeLetter, getStaffId } from '../../helpers/utils'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { days, ScheduleData, Staff, getDateOfWeekday, JobRoleForShift, PublishedSchedule, Availability, isDateInRange, getTimeOffMessage, getStaffAvailabilityForTheDay, ScheduleMetadata, checkIfShiftAm } from '../../helpers/workTimeScheduleHelper'
import { NO_ROLES_ASSIGNED } from '../../constants/strings'
import { StyleDetails } from '../../constants/staticTypes'
import { useQueryClient } from 'react-query'
import CustomTooltip from '../manage_work_schedule/CustomTooltip'
import dayjs from 'dayjs'
import CustomEventListForTable from '../manage_work_schedule/CustomEventListForTable'
import { createElement } from 'react'
import { Badge } from 'primereact/badge'
import { JTranslation } from '../../helpers/jTranslate'

type Props = {
    isLoading: boolean;
    selectedWeek: dayjs.Dayjs;
    staffsWithSchedule: ScheduleData | null;
    swapShiftRequest: (shift: PublishedSchedule, staff: Staff) => void;
    parent?: string;
    getRoleColorCode: (roleId: string) => StyleDetails;
    scheduleMetadata: ScheduleMetadata | null;
    viewType?: 'amPm' | 'normal'
}

type GetStaffAvailability = {
    day: string
    availabilities: Availability | undefined
}

const SwapScheduleDesktopView = ({
    isLoading,
    selectedWeek,
    staffsWithSchedule,
    swapShiftRequest,
    parent,
    getRoleColorCode,
    scheduleMetadata,
    viewType
}: Readonly<Props>) => {
    const queryClient = useQueryClient()
    const staffId = getStaffId()
    const { staffs, mandatoryDays, holidays } = staffsWithSchedule!
    const staffSortByCurrentUser = staffs.reduce<Staff[]>((staffArray, staff) => {
        if (staff.id === staffId) {
            return [staff, ...staffArray];
        } else {
            return [...staffArray, staff];
        }
    }, [])
        .filter((staff) => Object.values(staff.shifts).length);// Filter staff list if no shifts assigned/scheduled and sort with first name

    if (parent === 'myScheduleView') {
        staffSortByCurrentUser.sort((a, b) => a.firstName.localeCompare(b.firstName));
    }

    const managePermission = checkIfManageSwapRequest(queryClient)

    const staffNameTemplate = (rowData: Staff) => {
        const { preferredName = '', jobRoles = [] } = rowData ?? {}
        const roles = jobRoles
            .map((role: JobRoleForShift | undefined) => role?.jobRole?.jobRoleName)
            .filter(Boolean)
            .join(', ')

        return (
            <div className="text-nowrap">
                <div className="overflow-ellipsis fw-bold">{capitalizeFirstLetter(preferredName) || preferredName}</div>
                <div className="overflow-ellipsis" title={roles}>
                    {roles || NO_ROLES_ASSIGNED}
                </div>
            </div>
        )
    }

    const cellBodyTemplate = ({ day, rowData }: { day: string; rowData: Staff }) => {
        const { shifts, preferredName, unavailableDays, availabilities } = rowData
        const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' })
        const availabilityText = getStaffAvailabilityForTheDay({ day, availabilities, selectedWeek, type: 'schedule' })

        const unavailabilityArray = unavailableDays.filter((unavailableDay) => isDateInRange({ date: date, startDate: unavailableDay.startDate, endDate: unavailableDay.endDate }))
        const unavailability = unavailabilityArray.filter((item) => (item.actionStatus === 'APPROVED' || item.actionStatus === 'PENDING'))
        const shiftsArray = shifts[date]

        const timeOffText = getTimeOffMessage({ unavailability: unavailability, type: 'schedule' }, date)?.length
            ? getTimeOffMessage({ unavailability: unavailability, type: 'schedule' }, date)
            : availabilityText

        if (shiftsArray?.length) {

            // Sort the shiftsArray based on startTime and endTime 
            const sortedShifts = shiftsArray.sort((a, b) => {
                const startA = dayjs(a.assignedShifts.startTime, "hh:mm A");
                const startB = dayjs(b.assignedShifts.startTime, "hh:mm A");
                const endA = dayjs(a.assignedShifts.endTime, "hh:mm A");
                const endB = dayjs(b.assignedShifts.endTime, "hh:mm A");

                // Compare start times first; if they're equal, compare end times
                if (startA.isSame(startB)) {
                    return endA.isBefore(endB) ? -1 : 1;
                }
                return startA.isBefore(startB) ? -1 : 1;
            });

            const shiftTemplate = createElement('span', { children: [<></>] });
            const shiftAMTemplate = createElement('div', { className: 'w-100 flex-grow-1 d-flex flex-column align-items-center', children: [<></>] });
            const shiftPMTemplate = createElement('div', { className: 'w-100 flex-grow-1 d-flex flex-column align-items-center', children: [<></>] });

            sortedShifts.forEach((shift, index) => {
                const updatedShift = customDeepClone(shift) as PublishedSchedule
                const { startTime, endTime, isBd, isClose, role, isPoolRequested, roleId } = updatedShift.assignedShifts
                const canRequestSwap = (managePermission && !allowSwapRequest(updatedShift)) || (
                    !managePermission && (
                        rowData.id !== staffId ||
                        !allowSwapRequest(updatedShift)
                    )
                )

                const styleDetails = getRoleColorCode(roleId);
                const { backgroundColor, color } = styleDetails;

                const parsedDate = getDateOfWeekday({ selectedWeek, day, format: 'YYYY-MM-DD' })
                const isAmShift = checkIfShiftAm(startTime)

                const startTimeParsed = dayjs(startTime, 'hh:mm A');
                const endTimeParsed = dayjs(endTime, 'hh:mm A');

                const shiftCustomTemplate = (
                    <CustomTooltip
                        key={`swap-custom-tooltip-${rowData?.id}-${shift?.id}-${parsedDate}`}
                        staffName={preferredName}
                        showDelay={500}
                        content={sortedShifts}
                        showOnlyHovered={true}
                        currentShift={shift}
                    >
                        <div
                            id={`custom-tooltip-${rowData?.id}-${shift?.id}-${parsedDate}`}
                            className="scheduler-shift-card-body"
                            style={{
                                backgroundColor: "white",
                                border: `2px solid ${backgroundColor}`,
                            }}
                            onClick={() => {
                                if (parent !== 'myScheduleView') {
                                    if (canRequestSwap) return
                                    swapShiftRequest(updatedShift, rowData)
                                }
                            }}
                        >
                            <div className="d-flex">
                                <div className="flex-grow-0 d-flex align-items-center justify-center">
                                    <span
                                        className="shift-role"
                                        style={{
                                            backgroundColor,
                                            color: color,
                                        }}
                                    >{getFirstLetterUpperCase(role ?? '')}</span>
                                </div>
                                <div
                                    className="flex-grow-1 shift-name-main text-nowrap"
                                    role='button'
                                >
                                    {viewType === 'amPm'
                                        ? (
                                            <>
                                                {isClose ? `${startTimeParsed.minute() === 0
                                                    ? startTimeParsed.format('h')
                                                    : startTimeParsed.format('h:m')
                                                    } - CL` : ""}
                                                {isBd ? `${startTimeParsed.minute() === 0
                                                    ? startTimeParsed.format('h')
                                                    : startTimeParsed.format('h:m')
                                                    } - V` : ""}
                                                {!isBd && !isClose ? `${startTimeParsed.minute() === 0
                                                    ? startTimeParsed.format('h')
                                                    : startTimeParsed.format('h:m')
                                                    } - ${endTimeParsed.minute() === 0
                                                        ? endTimeParsed.format('h')
                                                        : endTimeParsed.format('h:m')
                                                    }` : ""
                                                }
                                            </>
                                        )
                                        : (
                                            <>
                                                {isClose ? `${startTime} - Close` : ""}
                                                {isBd ? `${startTime} - V` : ""}
                                                {!isBd && !isClose ? `${startTime} - ${endTime}` : ""}
                                            </>
                                        )
                                    }
                                </div>
                                {(parent !== 'myScheduleView' && canRequestSwap && isPoolRequested) && (
                                    <div className="flex-grow-0 d-flex align-items-center justify-center swap-icn-bg">
                                        <i className="pi pi-arrow-right-arrow-left text-light mx-1"></i>
                                    </div>
                                )}
                            </div>
                        </div>
                    </CustomTooltip>
                )

                if (viewType === 'amPm') {
                    if (isAmShift) {
                        shiftAMTemplate.props.children.push(shiftCustomTemplate);
                    } else {
                        shiftPMTemplate.props.children.push(shiftCustomTemplate);
                    }
                } else {
                    shiftTemplate.props.children.push(shiftCustomTemplate);
                }
            });

            return viewType === 'amPm'
                ? (
                    <>
                        {timeOffText}
                        <div className='d-flex gap-1'>{shiftAMTemplate}{shiftPMTemplate}</div>
                    </>
                ) : (
                    <>
                        {timeOffText}
                        {shiftTemplate}
                    </>
                );

            // return shiftsArray.map((shift, index) => {
            //     const updatedShift = customDeepClone(shift) as PublishedSchedule
            //     const { shiftName, startTime, endTime, isBd, isClose, role, isPoolRequested, roleId } = updatedShift.assignedShifts
            //     const canRequestSwap = (managePermission && !allowSwapRequest(updatedShift)) || (
            //         !managePermission && (
            //             rowData.id !== staffId ||
            //             !allowSwapRequest(updatedShift)
            //         )
            //     )

            //     const styleDetails = getRoleColorCode(roleId);
            //     const { backgroundColor, color } = styleDetails;

            //     return (
            //         <>
            //             {(index === 0 && timeOffText) && timeOffText}
            //             <CustomTooltip key={`desktop-swap-shift-card-body-${shift?.id}`} staffName={preferredName} showDelay={500} content={shiftsArray}>
            //                 <div
            //                     className={`scheduler-shift-card-body w-100`}
            //                     style={{
            //                         backgroundColor: "white",
            //                         border: `2px solid ${backgroundColor}`,
            //                     }}
            //                     onClick={() => {
            //                         if(parent !== 'myScheduleView'){
            //                             if (canRequestSwap) return
            //                             swapShiftRequest(updatedShift, rowData)
            //                         }
            //                     }}
            //                 >
            //                     <div className="d-flex">
            //                         <div className="flex-grow-0 d-flex align-items-center justify-center">
            //                             <span 
            //                                 className="shift-role"
            //                                 style={{
            //                                     backgroundColor,
            //                                     color: color,
            //                                 }}
            //                             >{getFirstLetterUpperCase(role ?? '')}</span>
            //                         </div>
            //                         <div className="flex-grow-1 shift-name-main text-nowrap">
            //                             {/* <span className="shift-name">{shiftName ?? ''}</span> */}
            //                             {isClose ? `${startTime} - Close` : ""}
            //                             {isBd ? `${startTime} - V` : ""}
            //                             {!isBd && !isClose ? `${startTime} - ${endTime}` : ''}
            //                         </div>
            //                         {(parent !== 'myScheduleView' && canRequestSwap && isPoolRequested) && (
            //                             <div className="flex-grow-0 d-flex align-items-center justify-center swap-icn-bg">
            //                                 <i className="pi pi-arrow-right-arrow-left text-light mx-1"></i>
            //                             </div>
            //                         )}
            //                     </div>
            //                 </div>
            //             </CustomTooltip >
            //         </>
            //     )
            // })
        } else {
            return timeOffText ?? (<></>)
        }
    }

    const getAmPmStaffCount = (day: string) => {
        const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' })
        const filteredStaffs = staffsWithSchedule?.staffs.filter(staff => Object.keys(staff.shifts).length > 0)
        const filteredStaffsByDay = filteredStaffs?.filter(staff => staff.shifts[date]?.length > 0)

        let amCount = 0;
        let pmCount = 0;

        filteredStaffsByDay?.forEach(staff => {
            const shifts = staff?.shifts[date];
            const amShifts = shifts?.filter(shift => shift.assignedShifts.startTime.includes('AM'));
            const pmShifts = shifts?.filter(shift => shift.assignedShifts.startTime.includes('PM'));
            amCount += amShifts?.length ? 1 : 0;
            pmCount += pmShifts?.length ? 1 : 0;
        })
        return { am: amCount, pm: pmCount }
    }

    const renderColumns = () => {
        return days.map((day, index) => {
            const amPmCount = getAmPmStaffCount(day);
            return (
                <Column
                    key={`${day}-${index + 1}`}
                    headerClassName="workTimeScheduleHeader p-0"
                    header={() => {
                        const checkHoliday = () => {
                            const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' })
                            const isHolidays = holidays.filter((holiday) => isDateInRange({ date: date, startDate: holiday.startDate, endDate: holiday.endDate })).length > 0
                            return isHolidays
                        };

                        const checkMandatoryDays = () => {
                            const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' })
                            const isMandatory = mandatoryDays.filter((mandatoryDay) => isDateInRange({ date: date, startDate: mandatoryDay.startDate, endDate: mandatoryDay.endDate })).length > 0
                            return isMandatory
                        };

                        return (
                            <div className='d-flex flex-column align-items-center justify-content-center position-relative'>
                                <div className="scheduler-header w-100" style={{ height: `120px` }}>
                                    <div className='mb-0 text-center text-nowrap fw-normal text-muted'>{getFirstThreeLetter(day)}</div>
                                    <div className="mb-2 text-center text-nowrap">{getDateOfWeekday({ selectedWeek, day: day })}</div>
                                    {/* rendering holidays */}
                                    {checkHoliday() && (
                                        <div className={`${checkHoliday() && 'holiday-header'}`}>
                                            <div className='mb-1 text-center text-nowrap fw-normal text-muted w-100'>
                                                <div className="d-flex align-center justify-content-center position-relative">
                                                    <div><JTranslation typeCase="none" text={"Holiday"} /></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {/* rendering mandatory days */}
                                    {checkMandatoryDays() && (
                                        <div className={`${checkMandatoryDays() && 'mandatory-day'}`}>
                                            <div className='mb-1 text-center text-nowrap fw-normal text-muted w-100'>
                                                <div className="d-flex align-center justify-content-center position-relative">
                                                    <div><JTranslation typeCase="none" text={"Mandatory Day"} /></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <CustomEventListForTable
                                        customEvents={scheduleMetadata?.customEvents ?? []}
                                        day={day}
                                    />
                                </div>
                                {viewType === 'amPm' && (
                                    <div className='w-100 row border-top' style={{ height: '30px' }}>
                                        <div className='col-6 d-flex align-items-center justify-content-center'>
                                            <span>AM</span>
                                            <Badge
                                                value={amPmCount?.am}
                                                style={{ fontSize: '0.65rem', minWidth: '1.05rem', height: '1rem', lineHeight: '1rem' }}
                                                className='ms-1'
                                            />
                                        </div>
                                        <div className='col-6 d-flex align-items-center justify-content-center'>
                                            <span>PM</span>
                                            <Badge
                                                value={amPmCount?.pm}
                                                style={{ fontSize: '0.65rem', minWidth: '1.05rem', height: '1rem', lineHeight: '1rem' }}
                                                className='ms-1'
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    }}
                    body={(data) => cellBodyTemplate({ day, rowData: data })}
                    style={{ minWidth: `${viewType === 'amPm' ? '15rem' : '12rem'}` }}
                    className="shift-column"
                />
            )
        })
    }

    return (
        <div className="card h-100">
            <DataTable
                value={staffSortByCurrentUser}
                showGridlines
                scrollable
                scrollHeight="100%"
                className={`h-100 work-time-schedule-table ${parent !== 'myScheduleView' ? 'bg-active-user' : ''}`}
                loading={isLoading}
                pt={{ loadingOverlay: { className: 'color-primary-custom', style: { zIndex: 3 } } }}
            >
                <Column
                    field="day"
                    frozen
                    className="overflow-ellipsis"
                    header={''}
                    body={staffNameTemplate}
                    style={{ minWidth: `${viewType === 'amPm' ? '15rem' : '12rem'}` }}
                    headerStyle={{ height: `120px'}` }}
                />
                {renderColumns()}
            </DataTable>
        </div>
    )
}

export default SwapScheduleDesktopView
